import { type FC } from "react";

import { clsx } from "clsx";

import { PAGE_BUCKETS } from "../billing_palette";
import { PageBucketBars, type PageBucketRow } from "../page_bucket_bars";
import { PageCompositionSubline } from "./composition_subline";
import { SectionHeading } from "./section_heading";
import type { PageCounts } from "./types";

export const PagesSection: FC<{
  counts: PageCounts;
  display: "stat-row" | "bars";
  primary: boolean;
  // Optional section heading override — defaults to "Pages"; the
  // Records-processed card passes "Pages processed" to match the
  // "Records processed" card-header voice and the `23 pages processed`
  // footnote phrasing used on the Billing By Project drill-in.
  heading?: string;
  // Server-provided composition values. When `condensedPageCount > 0`,
  // a sub-line `X full · Y condensed (N×)` renders below the section
  // heading per spec §9.4.1 (mockup D2). All three numbers come
  // straight from GraphQL — no client-side derivation.
  fullPageCount?: number;
  condensedPageCount?: number;
  condensedMultiplier?: number;
}> = ({
  counts,
  display,
  primary,
  heading = "Pages",
  fullPageCount,
  condensedPageCount,
  condensedMultiplier,
}) => {
  const hasCondensedPages = (condensedPageCount ?? 0) > 0;
  if (display === "stat-row") {
    const sizeClass = primary ? "text-base" : "text-sm";
    const totalClass = primary ? "text-lg font-bold" : "font-semibold";
    return (
      <div>
        <SectionHeading>{heading}</SectionHeading>
        <div className={clsx(sizeClass, "leading-relaxed text-slate-700")}>
          <strong className={clsx(totalClass, "text-slate-900")}>
            {counts.total}
          </strong>{" "}
          pages <span className="text-slate-400">— tagged as</span>{" "}
          {PAGE_BUCKETS.map((bucket, idx) => {
            const count = counts[bucket.key];
            return (
              <span key={bucket.key}>
                <strong className={count === 0 ? "text-slate-400" : ""}>
                  {count}
                </strong>{" "}
                {bucket.label.toLowerCase()}
                {idx < PAGE_BUCKETS.length - 1 && (
                  <span className="mx-1.5 text-slate-300">·</span>
                )}
              </span>
            );
          })}
        </div>
        {hasCondensedPages && (
          <div className="mt-1 text-[11px] font-medium text-slate-500">
            <PageCompositionSubline
              full={fullPageCount!}
              condensed={condensedPageCount!}
              multiplier={condensedMultiplier!}
            />
          </div>
        )}
      </div>
    );
  }

  const rows: PageBucketRow[] = PAGE_BUCKETS.map((b) => ({
    key: b.key,
    label: b.label,
    count: counts[b.key],
  }));

  return (
    <div>
      <SectionHeading
        trailing={
          <span className="text-[11px] font-normal tracking-normal text-slate-400 normal-case">
            {counts.total} total · multi-tag
          </span>
        }
      >
        {heading}
      </SectionHeading>
      {hasCondensedPages && (
        <div className="mb-2 text-[11px] font-medium text-slate-500">
          <PageCompositionSubline
            full={fullPageCount!}
            condensed={condensedPageCount!}
            multiplier={condensedMultiplier!}
          />
        </div>
      )}
      <PageBucketBars total={counts.total} rows={rows} />
      <p className="mt-2.5 text-[11px] text-slate-500">
        Some pages carry more than one tag, so bars are independent measurements
        (not slices of a whole).
      </p>
    </div>
  );
};
